import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  MapPin,
  Activity,
  Shield,
  Zap,
  Radio,
  AlertTriangle,
  ChevronRight,
  Signal
} from 'lucide-react';

const WorldMap = ({ deployments, selectedRegion, onSelectRegion }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const w = canvas.width;
    const h = canvas.height;

    let animFrame;
    let tick = 0;

    const draw = () => {
      tick++;
      ctx.clearRect(0, 0, w, h);

      ctx.strokeStyle = 'rgba(0, 255, 65, 0.06)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < w; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += 30) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(0, 255, 65, 0.25)';
      ctx.fillStyle = 'rgba(0, 255, 65, 0.04)';
      ctx.lineWidth = 1;

      const continents = [
        [[120,60],[180,50],[220,55],[260,70],[270,100],[260,130],[230,150],[200,170],[170,160],[140,130],[120,100]],
        [[200,180],[220,175],[240,190],[250,220],[245,260],[230,290],[210,300],[195,280],[190,240],[195,200]],
        [[360,55],[380,50],[410,55],[420,70],[415,90],[400,100],[380,95],[365,85],[355,70]],
        [[360,110],[390,100],[420,110],[430,140],[425,180],[410,210],[385,220],[365,210],[355,180],[350,140]],
        [[420,40],[460,35],[510,40],[550,50],[570,70],[560,100],[540,120],[510,110],[470,100],[440,90],[425,70]],
        [[520,200],[550,195],[570,205],[575,225],[560,240],[535,245],[520,230],[515,215]],
      ];

      continents.forEach(points => {
        ctx.beginPath();
        ctx.moveTo(points[0][0], points[0][1]);
        points.forEach(([x, y]) => ctx.lineTo(x, y));
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      });

      deployments.forEach((dep, i) => {
        const x = dep.x * w / 700;
        const y = dep.y * h / 350;
        const isSelected = selectedRegion === dep.region;
        const pulseSize = 4 + Math.sin(tick * 0.05 + i) * 2;

        if (dep.status === 'active') {
          const ringSize = 8 + Math.sin(tick * 0.03 + i) * 4;
          ctx.strokeStyle = `rgba(0, 255, 65, ${0.3 + Math.sin(tick * 0.05 + i) * 0.2})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, ringSize, 0, Math.PI * 2);
          ctx.stroke();
        }

        const color = dep.status === 'active' ? '#00FF41' :
          dep.status === 'degraded' ? '#FFB800' : '#FF003C';
        
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = isSelected ? 20 : 10;
        ctx.beginPath();
        ctx.arc(x, y, isSelected ? pulseSize + 2 : pulseSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        if (isSelected || dep.agents > 2) {
          ctx.fillStyle = 'rgba(0, 255, 65, 0.7)';
          ctx.font = '9px "Share Tech Mono", monospace';
          ctx.textAlign = 'center';
          ctx.fillText(dep.label, x, y - 12);
          ctx.fillText(`${dep.agents} AGENTS`, x, y + 16);
        }
      });

      const activeDeployments = deployments.filter(d => d.status === 'active');
      ctx.strokeStyle = `rgba(0, 255, 65, ${0.05 + Math.sin(tick * 0.02) * 0.03})`;
      ctx.lineWidth = 0.5;
      ctx.setLineDash([4, 8]);
      for (let i = 0; i < activeDeployments.length; i++) {
        for (let j = i + 1; j < activeDeployments.length; j++) {
          const a = activeDeployments[i];
          const b = activeDeployments[j];
          ctx.beginPath();
          ctx.moveTo(a.x * w / 700, a.y * h / 350);
          ctx.lineTo(b.x * w / 700, b.y * h / 350);
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);

      const scanY = (tick * 0.5) % h;
      const scanGradient = ctx.createLinearGradient(0, scanY - 2, 0, scanY + 2);
      scanGradient.addColorStop(0, 'rgba(0, 255, 65, 0)');
      scanGradient.addColorStop(0.5, 'rgba(0, 255, 65, 0.15)');
      scanGradient.addColorStop(1, 'rgba(0, 255, 65, 0)');
      ctx.fillStyle = scanGradient;
      ctx.fillRect(0, scanY - 2, w, 4);

      animFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animFrame);
  }, [deployments, selectedRegion]);

  const handleClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    const clicked = deployments.find(dep => {
      const x = dep.x * canvas.width / 700;
      const y = dep.y * canvas.height / 350;
      return Math.hypot(clickX - x, clickY - y) < 20;
    });

    onSelectRegion(clicked?.region || null);
  };

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={350}
      className="w-full h-auto cursor-crosshair"
      onClick={handleClick}
      data-testid="war-room-map"
    />
  );
};

const WarRoom = ({ agents }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const deployments = [
    { region: 'na-east', label: 'NA-EAST', x: 200, y: 100, agents: Math.max(1, Math.floor(agents.length * 0.3)), status: 'active', latency: 12 },
    { region: 'na-west', label: 'NA-WEST', x: 140, y: 90, agents: Math.max(1, Math.floor(agents.length * 0.2)), status: 'active', latency: 18 },
    { region: 'eu-central', label: 'EU-CENT', x: 390, y: 70, agents: Math.max(1, Math.floor(agents.length * 0.2)), status: 'active', latency: 45 },
    { region: 'asia-pac', label: 'ASIA-PAC', x: 530, y: 85, agents: Math.max(0, Math.floor(agents.length * 0.15)), status: agents.length > 3 ? 'active' : 'degraded', latency: 120 },
    { region: 'sa-east', label: 'SA-EAST', x: 230, y: 240, agents: Math.max(0, Math.floor(agents.length * 0.1)), status: agents.length > 5 ? 'active' : 'offline', latency: 85 },
    { region: 'af-south', label: 'AF-SOUTH', x: 390, y: 180, agents: Math.max(0, Math.floor(agents.length * 0.05)), status: agents.length > 8 ? 'active' : 'offline', latency: 200 },
  ];

  const selectedDep = deployments.find(d => d.region === selectedRegion);
  const totalDeployed = deployments.reduce((sum, d) => sum + d.agents, 0);
  const activeRegions = deployments.filter(d => d.status === 'active').length;

  return (
    <div className="space-y-6" data-testid="war-room">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-military text-2xl text-primary flex items-center gap-3">
            <Globe className="w-7 h-7" />
            WAR ROOM
          </h2>
          <p className="text-primary/50 font-mono text-sm mt-1">Global Agent Deployment Operations</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1 border border-primary/20 bg-black/50">
            <Signal className="w-3 h-3 text-primary animate-pulse" />
            <span className="text-xs font-mono text-primary tracking-wider">
              {time.toUTCString().split(' ').slice(4).join(' ')} UTC
            </span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 cyber-card border border-primary/20 overflow-hidden">
          <div className="p-4 border-b border-primary/10 flex items-center justify-between">
            <h3 className="font-orbitron text-sm font-bold text-primary flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              GLOBAL DEPLOYMENT MAP
            </h3>
            <div className="flex items-center gap-4 text-[10px] font-mono">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary rounded-full" /> ACTIVE</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-full" /> DEGRADED</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" /> OFFLINE</span>
            </div>
          </div>
          <div className="p-4 bg-[#000500]">
            <WorldMap
              deployments={deployments}
              selectedRegion={selectedRegion}
              onSelectRegion={setSelectedRegion}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="cyber-card p-4 border border-primary/20">
            <h4 className="text-[10px] font-mono text-primary/50 tracking-wider mb-3">DEPLOYMENT SUMMARY</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-primary/70">REGIONS ACTIVE</span>
                <span className="text-lg font-orbitron text-primary">{activeRegions}/{deployments.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-primary/70">TOTAL DEPLOYED</span>
                <span className="text-lg font-orbitron text-primary">{totalDeployed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-primary/70">THREAT LEVEL</span>
                <span className="text-lg font-orbitron text-yellow-400">LOW</span>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {selectedDep ? (
              <motion.div
                key={selectedDep.region}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="cyber-card p-4 border border-primary/30"
                data-testid="region-detail"
              >
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-primary" />
                  <h4 className="font-orbitron text-sm font-bold text-primary">{selectedDep.label}</h4>
                </div>
                <div className="space-y-2 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-primary/50">STATUS</span>
                    <span className={
                      selectedDep.status === 'active' ? 'text-primary' :
                      selectedDep.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'
                    }>{selectedDep.status.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary/50">AGENTS</span>
                    <span className="text-primary">{selectedDep.agents}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary/50">LATENCY</span>
                    <span className="text-primary">{selectedDep.latency}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary/50">ENCRYPTION</span>
                    <span className="text-primary">AES-256</span>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="cyber-card p-4 border border-primary/10"
              >
                <p className="text-xs font-mono text-primary/30 text-center py-4">
                  SELECT A REGION ON THE MAP
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="cyber-card p-4 border border-primary/20">
            <h4 className="text-[10px] font-mono text-primary/50 tracking-wider mb-3">ALL REGIONS</h4>
            <div className="space-y-2">
              {deployments.map((dep) => (
                <button
                  key={dep.region}
                  onClick={() => setSelectedRegion(dep.region === selectedRegion ? null : dep.region)}
                  data-testid={`region-btn-${dep.region}`}
                  className={`w-full flex items-center justify-between p-2 text-xs font-mono transition-colors ${
                    selectedRegion === dep.region
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-primary/5 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      dep.status === 'active' ? 'bg-primary' :
                      dep.status === 'degraded' ? 'bg-yellow-400' : 'bg-red-500'
                    }`} />
                    <span className="text-primary/70">{dep.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-primary/50">{dep.agents}</span>
                    <ChevronRight className="w-3 h-3 text-primary/30" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="cyber-card p-4 border border-primary/20">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono text-primary/70">GLOBAL SHIELD</span>
              <span className="text-xs font-mono text-primary">ACTIVE</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono text-primary/70">UPTIME</span>
              <span className="text-xs font-mono text-primary">99.97%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-primary" />
            <span className="text-xs font-mono text-primary/50">MESH NETWORK: {activeRegions} NODES // LATENCY: NOMINAL</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarRoom;
